import { useState, useEffect } from 'react'
import { Card } from './ui/card'
import { Switch } from './ui/switch'
import { Bluetooth, Eye, Clock, Globe, ShieldCheck, ExternalLink } from 'lucide-react'
import { PageShell, PageHeader } from './ui/PageShell'
import { useSettingsStore } from '../stores/settingsStore'
import { useTranslation } from '../locales'
import { useLanguageStore } from '../stores/languageStore'
import { Capacitor } from '@capacitor/core'

const selectArrowStyle: React.CSSProperties = {
  backgroundImage:
    "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23a1a1aa' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.75rem center',
  backgroundSize: '12px',
}

/**
 * 개인정보처리방침 호스팅 주소.
 * 네이티브 앱(Capacitor)에서는 로컬 webview 경로가 아닌 공개 URL을 열어야
 * Play Console 심사에서 요구하는 외부 접근 가능 방침 페이지와 동일해진다.
 */
const PRIVACY_POLICY_URL = 'https://linkband-sdk.looxidlabs.com/privacy-policy.html'

export function Settings() {
  const { timezone, setTimezone } = useSettingsStore()
  const { currentLanguage, setLanguage } = useLanguageStore()
  const t = useTranslation(currentLanguage)

  const [autoRegistrationEnabled, setAutoRegistrationEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('autoRegistrationEnabled')
    return saved ? JSON.parse(saved) : true
  })

  const [autoConnectionEnabled, setAutoConnectionEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('autoConnectionEnabled')
    return saved ? JSON.parse(saved) : true
  })

  useEffect(() => {
    localStorage.setItem('autoRegistrationEnabled', JSON.stringify(autoRegistrationEnabled))
  }, [autoRegistrationEnabled])

  useEffect(() => {
    localStorage.setItem('autoConnectionEnabled', JSON.stringify(autoConnectionEnabled))
  }, [autoConnectionEnabled])

  /**
   * 개인정보처리방침을 새 창/시스템 브라우저로 연다.
   * 웹에서는 번들과 함께 배포되는 /privacy-policy.html, 네이티브에서는 공개 URL 사용.
   */
  const openPrivacyPolicy = () => {
    const url = Capacitor.isNativePlatform()
      ? PRIVACY_POLICY_URL
      : '/privacy-policy.html'
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <PageShell>
      <PageHeader title={t.settings.title} subtitle={t.settings.subtitle} />

        {/* Language Settings */}
        <Card className="p-5 md:p-6 hover:bg-card/90 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
            <h3 className="font-medium">{t.settings.language}</h3>
          </div>
          <p className="t-soft mb-6">{t.settings.languageDesc}</p>

          <div
            role="group"
            aria-label={t.settings.language}
            className="inline-flex items-center rounded-full bg-white/[0.04] border border-white/10 p-1"
          >
            <button
              type="button"
              onClick={() => setLanguage('ko')}
              className={
                'px-4 py-1.5 text-sm rounded-full transition-colors ' +
                (currentLanguage === 'ko'
                  ? 'bg-indigo-500/80 text-white'
                  : 'text-[var(--fg-3)] hover:text-[var(--fg-1)]')
              }
              aria-pressed={currentLanguage === 'ko'}
            >
              {t.settings.languageKo}
            </button>
            <button
              type="button"
              onClick={() => setLanguage('en')}
              className={
                'px-4 py-1.5 text-sm rounded-full transition-colors ' +
                (currentLanguage === 'en'
                  ? 'bg-indigo-500/80 text-white'
                  : 'text-[var(--fg-3)] hover:text-[var(--fg-1)]')
              }
              aria-pressed={currentLanguage === 'en'}
            >
              {t.settings.languageEn}
            </button>
          </div>
        </Card>

        {/* Device Manager Settings */}
        <Card className="p-5 md:p-6 hover:bg-card/90 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <Bluetooth className="w-5 h-5 text-indigo-400" strokeWidth={1.5} />
            <h3 className="font-medium">{t.settings.deviceManager}</h3>
          </div>
          <p className="t-soft mb-6">{t.settings.deviceManagerDesc}</p>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h4 className="lb-body font-medium t-strong">{t.settings.autoConnect}</h4>
              <p className="t-soft">{t.settings.autoConnectDesc}</p>
            </div>
            <Switch checked={autoConnectionEnabled} onCheckedChange={setAutoConnectionEnabled} />
          </div>

          <hr className="border-white/6 my-6" />

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h4 className="lb-body font-medium t-strong">{t.settings.autoRegister}</h4>
              <p className="t-soft">{t.settings.autoRegisterDesc}</p>
            </div>
            <Switch
              checked={autoRegistrationEnabled}
              onCheckedChange={setAutoRegistrationEnabled}
            />
          </div>

          <hr className="border-white/6 my-6" />

          <div className="text-xs t-faint bg-white/[0.03] border border-white/6 p-2 md:p-3 rounded-lg">
            <p className="font-medium mb-1">{t.settings.deviceManagerInfo}</p>
            <ul className="space-y-1 text-xs">
              <li>• {t.settings.autoConnectInfo}</li>
              <li>• {t.settings.autoRegisterInfo}</li>
              <li>• {t.settings.manualModeInfo}</li>
              <li>• {t.settings.noDuplicateInfo}</li>
            </ul>
          </div>
        </Card>

        {/* Time Zone Settings */}
        <Card className="p-5 md:p-6 hover:bg-card/90 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-orange-400" strokeWidth={1.5} />
            <h3 className="font-medium">{t.settings.timeZone}</h3>
          </div>
          <p className="t-soft mb-6">{t.settings.timeZoneDesc}</p>

          <div className="space-y-4">
            <label className="block lb-body font-medium t-strong">{t.settings.timeZoneLabel}</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value as 'system' | 'korea' | 'utc')}
              style={selectArrowStyle}
              className="w-full appearance-none px-3 py-2 pr-9 border border-white/6 bg-white/[0.03] text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="system">{t.settings.timeZoneSystem}</option>
              <option value="korea">{t.settings.timeZoneKorea}</option>
              <option value="utc">{t.settings.timeZoneUtc}</option>
            </select>
            <p className="text-xs t-faint">{t.settings.timeZoneNote}</p>
          </div>

          <hr className="border-white/6 my-6" />

          <div className="text-xs t-faint bg-white/[0.03] border border-white/6 p-2 md:p-3 rounded-lg">
            <p className="font-medium mb-1">{t.settings.timeZoneInfo}</p>
            <ul className="space-y-1 text-xs">
              <li>• {t.settings.timeZoneSystemInfo}</li>
              <li>• {t.settings.timeZoneKoreaInfo}</li>
              <li>• {t.settings.timeZoneUtcInfo}</li>
              <li>• {t.settings.timeZoneApplyInfo}</li>
            </ul>
          </div>
        </Card>

        {/* Visualizer Settings */}
        <Card className="p-5 md:p-6 hover:bg-card/90 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-5 h-5 text-purple-400" strokeWidth={1.5} />
            <h3 className="font-medium">{t.settings.visualizer}</h3>
          </div>
          <p className="t-soft mb-6">{t.settings.visualizerDesc}</p>

          <div className="t-faint py-4">{t.settings.visualizerComingSoon}</div>
        </Card>

        {/* Privacy Policy — 외부 공개 방침 페이지 링크 (Play Console 요구사항) */}
        <Card className="p-5 md:p-6 hover:bg-card/90 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
            <h3 className="font-medium">
              {currentLanguage === 'ko' ? '개인정보처리방침' : 'Privacy Policy'}
            </h3>
          </div>
          <p className="t-soft mb-6">
            {currentLanguage === 'ko'
              ? '수집 항목·저장 위치·외부 전송 조건을 확인할 수 있습니다.'
              : 'Review what is collected, where it is stored, and when it is transmitted externally.'}
          </p>

          <button
            type="button"
            onClick={openPrivacyPolicy}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/6 bg-white/[0.03] hover:bg-white/[0.06] transition-colors lb-body font-medium t-strong focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
            {currentLanguage === 'ko' ? '개인정보처리방침 열기' : 'Open Privacy Policy'}
          </button>
        </Card>
    </PageShell>
  )
}
